import {createApp} from 'vue';
import {createHead} from '@vueuse/head';
import axios from 'axios';
import App from './App.vue';
import './index.css';
import router from './router';
import Cookies from 'universal-cookie';
import {showErrorToast, showSuccessToast} from '@/composables/useToast';

// Set the base URL and credentials for Axios requests
axios.defaults.baseURL = process.env.VUE_APP_API_BASE_URL;
axios.defaults.withCredentials = true;

// Create a Cookies instance to manage browser cookies
const cookies = new Cookies();

// Get the access token from cookies, if it exists
const token = cookies.get('access_token');

// If a token is found, add it to Axios headers for authorization.
if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

// Add an Axios interceptor to include the token in every request
axios.interceptors.request.use(config => {
    const token = cookies.get('access_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

const isMutationMethod = (method?: string): boolean => {
    if (!method) return false;
    const verb = method.toLowerCase();
    return verb === 'post' || verb === 'put' || verb === 'patch' || verb === 'delete';
};

const shouldShowAdminToast = (method?: string): boolean => {
    if (typeof window === 'undefined') return false;
    if (!window.location.pathname.startsWith('/administrator')) return false;
    return isMutationMethod(method);
};

const getSuccessMessage = (method?: string): string => {
    const verb = (method || '').toLowerCase();
    if (verb === 'post') return 'Created successfully.';
    if (verb === 'delete') return 'Deleted successfully.';
    return 'Updated successfully.';
};

axios.interceptors.response.use(
    (response) => {
        if (shouldShowAdminToast(response.config.method)) {
            showSuccessToast(getSuccessMessage(response.config.method));
        }
        return response;
    },
    (error) => {
        const method = error?.config?.method as string | undefined;
        if (shouldShowAdminToast(method)) {
            const message =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                'Action failed. Please try again.';
            showErrorToast(String(message));
        }
        return Promise.reject(error);
    }
);

// Create the Vue app and set up plugins
const app = createApp(App);
const head = createHead();
app.use(router);
app.use(head);

// Start the Vue app by mounting it to the DOM
app.mount('#app');
